import React, { useState } from 'react';
import {
  Container,
  TextField,
  Typography,
  Paper,
  Grid,
  Box,
  Avatar,
  Input,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close'; // Import Close icon
import EditIcon from '@mui/icons-material/Edit'; // Edit icon for editing bill info
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import 'jspdf-autotable';
import { useSnackbar } from 'notistack';
import StaplefordLogo from '../logo.png'

const InvoiceGenerator = () => {
  const [items, setItems] = useState([{ description: '', quantity: '', price: '' }]);
  const [mileage, setMileage] = useState('');
const [registrationNumber, setRegistrationNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [billTo, setBillTo] = useState({
    name: '',
    company: '',
    country: '',
    email: '',
    phone: '',
    address: ''
  });
  const [notes, setNotes] = useState('');
  const [openBillDialog, setOpenBillDialog] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleItemChange = (index, event) => {
    const values = [...items];
    values[index][event.target.name] = event.target.value;
    setItems(values);
  };

  const handleAddItem = () => {
    setItems([...items, { description: '', quantity: '', price: '' }]);
  };

  const handleRemoveItem = (index) => {
    const values = items.filter((_, i) => i !== index);
    setItems(values);
    enqueueSnackbar('Item removed', { variant: 'info' });
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => 
      sum + (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0), 0
    );
  };

  const subtotal = calculateTotal(items);
  const vatRate = 0.20;
  const vatAmount = subtotal * vatRate;
  const totalWithVAT = subtotal + vatAmount;

 const generatePDF = () => {
  setLoading(true);

  const img = new Image();
  img.src = StaplefordLogo;

  img.onload = () => {
    try {
      const doc = new jsPDF();

      // ===== Watermark (unchanged) =====
      const imgWidth = 150;
      const imgHeight = 75;
      const x = (doc.internal.pageSize.width - imgWidth) / 2 + 50;
      const y = (doc.internal.pageSize.height - imgHeight) / 2 - 20;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = imgWidth;
      canvas.height = imgHeight;
      ctx.globalAlpha = 0.08;
      ctx.drawImage(img, 0, 0, imgWidth, imgHeight);
      const dataURL = canvas.toDataURL('image/png');
      doc.addImage(dataURL, 'PNG', x, y, imgWidth, imgHeight, undefined, 'NONE', 45);

      // ===== Header Branding =====
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(0, 51, 102);
      doc.text('STAPLEFORD AUTOCENTRE', 14, 25);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(90, 90, 90);
      doc.text('Victoria St, Stapleford', 14, 35);
      doc.text('Nottingham NG9 7AP', 14, 42);
      doc.text('United Kingdom', 14, 49);

      // ===== Dates formatting =====
      const invoiceDateFormatted = format(new Date(invoiceDate), 'dd/MM/yyyy') || 'N/A';
      const dueDateFormatted = format(new Date(dueDate), 'dd/MM/yyyy') || 'N/A';

      // ===== Invoice Details =====
      doc.autoTable({
        startY: 60,
        head: [['Invoice Number', 'Invoice Date', 'Due Date']],
        body: [[invoiceNumber || 'N/A', invoiceDateFormatted, dueDateFormatted]],
        theme: 'grid',
        styles: { halign: 'center', fontSize: 11 },
        headStyles: {
          fillColor: [0, 102, 204],
          textColor: [255, 255, 255],
          halign: 'center',
          fontStyle: 'bold'
        },
        bodyStyles: { fillColor: [245, 250, 255] },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 60 }
        }
      });

      // ===== Vehicle Details =====
      doc.autoTable({
        startY: doc.lastAutoTable.finalY,
        head: [['Mileage', 'Registration Number', 'VAT Number']],
        body: [[mileage || 'N/A', registrationNumber || 'N/A', '494 8447 35']],
        theme: 'grid',
        styles: { halign: 'center', fontSize: 11 },
        headStyles: {
          fillColor: [0, 102, 204],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        bodyStyles: { fillColor: [245, 250, 255] },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 60 }
        }
      });

      // ===== Bill To Section =====
      const billToData = [
        ['Name', billTo.name || ''],
        ['Company', billTo.company || ''],
        ['Country', billTo.country || ''],
        ['Email', billTo.email || ''],
        ['Phone', billTo.phone || ''],
        ['Address', billTo.address || '']
      ];

      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 12,
        head: [['Bill To', 'Details']],
        body: billToData,
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: {
          fillColor: [0, 102, 204],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        bodyStyles: { fillColor: [250, 253, 255] },
        columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 120 } },
        margin: { left: 14 }
      });

      // ===== Items Table =====
      const itemsTable = items.map(item => [
        item.description || '',
        item.quantity || '',
        item.price || '',
        ((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0)).toFixed(2) || '0.00'
      ]);

      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 15,
        head: [['Description', 'Quantity', 'Price', 'Total']],
        body: itemsTable,
        foot: [['', '', 'Subtotal:', subtotal.toFixed(2)]],
        theme: 'grid',
        headStyles: {
          fillColor: [0, 102, 204],
          textColor: [255, 255, 255]
        },
        bodyStyles: { textColor: [50, 50, 50], fillColor: [250, 253, 255] },
        footStyles: {
          fillColor: [220, 235, 255],
          textColor: [0, 51, 102],
          fontStyle: 'bold'
        }
      });

      // ===== VAT =====
     // ===== VAT (clean, right aligned row) =====
doc.autoTable({
  startY: doc.lastAutoTable.finalY + 5,
  body: [
    [
      { content: 'VAT (20%):', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
      { content: vatAmount.toFixed(2), styles: { halign: 'right', fontStyle: 'bold' } }
    ]
  ],
  theme: 'plain',
  styles: { fontSize: 11 },
  bodyStyles: { fillColor: [235, 243, 255], textColor: [0, 51, 102] },
  columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 40 }, 2: { cellWidth: 40 }, 3: { cellWidth: 40 } }
});
doc.autoTable({
  startY: doc.lastAutoTable.finalY + 5,
  body: [
    [
      { content: 'Total Amount Due:', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
      { content: totalWithVAT.toFixed(2), styles: { halign: 'right', fontStyle: 'bold' } }
    ]
  ],
  theme: 'plain',
  styles: { fontSize: 11 },
  bodyStyles: { fillColor: [235, 243, 255], textColor: [0, 51, 102] },
  columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 40 }, 2: { cellWidth: 40 }, 3: { cellWidth: 40 } }
});
// ===== Total (highlighted row, no glitch) =====


      // ===== Notes =====
      const notesStartY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(12);
      doc.setTextColor(0, 51, 102);
      doc.text('Notes:', 14, notesStartY);
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      doc.text(notes || 'N/A', 14, notesStartY + 10, { maxWidth: 180 });

      // ===== Footer (clean, without "thank you") =====
      const footerY = doc.internal.pageSize.height - 20;
      doc.setDrawColor(0, 102, 204);
      doc.setLineWidth(0.5);
      doc.line(14, footerY - 8, doc.internal.pageSize.width - 14, footerY - 8);

      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('Stapleford Autocentre | www.staplefordautocentre.com | +44 115 939 4617', 14, footerY);

      // ===== Generate & Download =====
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      setTimeout(() => {
        window.open(pdfUrl, '_blank');
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = 'invoice.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setLoading(false);
      }, 1500);

    } catch (error) {
      console.error('Error generating PDF:', error);
      enqueueSnackbar('Error generating PDF', { variant: 'error' });
      setLoading(false);
    }
  };

  img.onerror = () => {
    console.error('Failed to load logo image');
    enqueueSnackbar('Error: Failed to load logo image', { variant: 'error' });
    setLoading(false);
  };
};



  
  
  
      
  
  
  
  
  
  
  const handleOpenBillDialog = () => {
    setOpenBillDialog(true);
  };

  const handleCloseBillDialog = () => {
    setBillTo({ // Reset fields to empty on cancel
      name: '',
      company: '',
      country: '',
      email: '',
      phone: '',
      address: ''
    });
    setOpenBillDialog(false); // Close the dialog
  };

  const handleSaveBillTo = () => {
    setOpenBillDialog(false);
  };

  return (
    <section className='invoice-page mt-3'>
    <Container>
      <Paper className='invoice-dialogue' style={{ padding: '30px', marginBottom: '24px'}}>
        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Invoice Number"
              fullWidth
              margin="normal"
              variant="outlined"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              InputLabelProps={{ style: { fontSize: 17 ,color:'darkblue',fontWeight:600} }} // Increase label size
              InputProps={{ style: { fontSize: 16 } }} // Increase input text size
              focused // Keep input focused style
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Invoice Date"
              type="date"
              fullWidth
              margin="normal"
              variant="outlined"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              InputLabelProps={{ style: { fontSize: 17 ,color:'darkblue',fontWeight:600} }} // Increase label size
              InputProps={{ style: { fontSize: 16 } }} // Increase input text size
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Due Date"
              type="date"
              fullWidth
              margin="normal"
              variant="outlined"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{ style: { fontSize: 17 ,color:'darkblue',fontWeight:600} }} // Increase label size
              InputProps={{ style: { fontSize: 16 } }} // Increase input text size
            />
          </Grid>
          <Grid item xs={12} md={4}>
  <TextField
    label="Mileage"
    fullWidth
    margin="normal"
    variant="outlined"
    value={mileage}
    onChange={(e) => setMileage(e.target.value)}
    InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }}
    InputProps={{ style: { fontSize: 16 } }}
  />
</Grid>

<Grid item xs={12} md={4}>
  <TextField
    label="Registration Number"
    fullWidth
    margin="normal"
    variant="outlined"
    value={registrationNumber}
    onChange={(e) => setRegistrationNumber(e.target.value)}
    InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }}
    InputProps={{ style: { fontSize: 16 } }}
  />
</Grid>

        </Grid>

        <Grid container spacing={3} mb={2}>
          <Grid item xs={12}>
         <p> <span
  style={{
    display: 'inline-block',
    cursor: 'pointer',
    color: 'darkblue',
    fontWeight: '400',
    fontSize: 22,
  }}
  onClick={handleOpenBillDialog} // Move the click handler here
>
  Edit Bill To
  <EditIcon className='mx-3'
    style={{ cursor: 'pointer', color: 'darkblue', fontSize: 30 }}
  />
</span></p>

          </Grid>
        </Grid>

        {billTo.name && (
          <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
          <p><span style={{color:'darkblue', fontWeight:'600'}}>Bill To</span></p>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <p>Name : <span style={{color:'darkblue', fontWeight:'400'}}>{billTo.name}</span></p>
            </Grid>
            <Grid item xs={12} sm={6}>
              <p>Company : <span style={{color:'darkblue', fontWeight:'400'}}>{billTo.company}</span></p>
            </Grid>
            <Grid item xs={12} sm={6}>
              <p>Country : <span style={{color:'darkblue', fontWeight:'400'}}>{billTo.country}</span></p>
            </Grid>
            <Grid item xs={12} sm={6}>
              <p>Address : <span style={{color:'darkblue', fontWeight:'400'}}>{billTo.address}</span></p>
            </Grid>
            <Grid item xs={12} sm={6}>
              <p>Email : <span style={{color:'darkblue', fontWeight:'400'}}>{billTo.email}</span></p>
            </Grid>
            <Grid item xs={12} sm={6}>
              <p>Phone : <span style={{color:'darkblue', fontWeight:'400'}}>{billTo.phone}</span></p>
            </Grid>
          </Grid>
        </Paper>
        
        )}

        <Grid container spacing={3} mb={3}>
          {items.map((item, index) => (
           <Grid item xs={12} key={index} container spacing={1}>
           <Grid item xs={12} mt={2} sm={4}>
             <TextField
               label="Description"
               name="description"
               value={item.description}
               onChange={(e) => handleItemChange(index, e)}
               fullWidth
               InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }}
               InputProps={{ style: { fontSize: 16 } }}
             />
           </Grid>
           <Grid item xs={12} mt={2} sm={3}>
             <TextField
               label="Quantity"
               name="quantity"
               type="number"
               value={item.quantity}
               onChange={(e) => handleItemChange(index, e)}
               fullWidth
               InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }}
               InputProps={{ style: { fontSize: 16 } }}
             />
           </Grid>
           <Grid item xs={12} mt={2} sm={3}>
             <TextField
               label="Price"
               name="price"
               type="number"
               value={item.price}
               onChange={(e) => handleItemChange(index, e)}
               fullWidth
               InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }}
               InputProps={{ style: { fontSize: 16 } }}
             />
           </Grid>
           <Grid item xs={12} sm={2} mt={3}>
             <CloseIcon
               onClick={() => handleRemoveItem(index)}
               style={{ cursor: 'pointer', color: 'red', fontSize: 35, fontWeight: 'bolder' }}
             />
           </Grid>
         </Grid>
         
          ))}
        </Grid>

        <Box mt={2}>
          <span
            onClick={handleAddItem}
            style={{
              cursor: 'pointer',
              color: 'darkblue',
    fontWeight: '400',
    fontSize: 22,
            }}
          >
            Add Item
            <AddIcon className='mx-3'
    style={{ cursor: 'pointer', color: 'darkblue', fontSize: 30 }}
  />
          </span>
        </Box>

        <Box mt={3}>
          <p>Subtotal : <span style={{color:'darkblue',fontWeight:'600'}} className='mx-4'>£{subtotal.toFixed(2)}</span></p>
          <p>VAT (20%) : <span style={{color:'darkblue',fontWeight:'600'}} className='mx-4'>£{vatAmount.toFixed(2)}</span></p>
          <p>TotalDue : <span style={{color:'darkblue',fontWeight:'600'}} className='mx-4'>£{totalWithVAT.toFixed(2)}</span></p>
        </Box>

        <Box mt={2}>
          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            InputLabelProps={{ style: { fontSize: 17 ,color:'darkblue',fontWeight:600} }} // Increase label size
            InputProps={{ style: { fontSize: 16 } }} // Increase input text size
          />
        </Box>

        <Box mt={2}>
  {loading ? (
    <CircularProgress size={24} />
  ) : (
    <span
      onClick={generatePDF}
      style={{ cursor: 'pointer', color: 'darkblue', fontSize: 20 }}
    >
      Generate PDF
      <DescriptionIcon
        className='mx-3'
        style={{ cursor: 'pointer', color: 'darkblue', fontSize: 30 }}
      />
    </span>
  )}
</Box>

      </Paper>
      <Dialog 
       PaperProps={{
        className: 'custom-dialog',
      }} 
      BackdropProps={{
        className: 'custom-backdrop', // Apply custom backdrop class
      }}
      open={openBillDialog} onClose={handleCloseBillDialog}>
  <DialogContent>
    <TextField
      label="Name"
      fullWidth
      margin="normal"
      variant="outlined"
      value={billTo.name}
      onChange={(e) => setBillTo({ ...billTo, name: e.target.value })}
      InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }} // Label styles
      InputProps={{ style: { fontSize: 16 } }} // Input styles
    />
    <TextField
      label="Company"
      fullWidth
      margin="normal"
      variant="outlined"
      value={billTo.company}
      onChange={(e) => setBillTo({ ...billTo, company: e.target.value })}
      InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }} // Label styles
      InputProps={{ style: { fontSize: 16 } }} // Input styles
    />
    <TextField
      label="Country"
      fullWidth
      margin="normal"
      variant="outlined"
      value={billTo.country}
      onChange={(e) => setBillTo({ ...billTo, country: e.target.value })}
      InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }} // Label styles
      InputProps={{ style: { fontSize: 16 } }} // Input styles
    />
    <TextField
      label="Email"
      fullWidth
      margin="normal"
      variant="outlined"
      value={billTo.email}
      onChange={(e) => setBillTo({ ...billTo, email: e.target.value })}
      InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }} // Label styles
      InputProps={{ style: { fontSize: 16 } }} // Input styles
    />
    <TextField
      label="Phone"
      fullWidth
      margin="normal"
      variant="outlined"
      value={billTo.phone}
      onChange={(e) => setBillTo({ ...billTo, phone: e.target.value })}
      InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }} // Label styles
      InputProps={{ style: { fontSize: 16 } }} // Input styles
    />
    <TextField
      label="Address"
      fullWidth
      margin="normal"
      variant="outlined"
      value={billTo.address}
      onChange={(e) => setBillTo({ ...billTo, address: e.target.value })}
      InputLabelProps={{ style: { fontSize: 17, color: 'darkblue', fontWeight: 600 } }} // Label styles
      InputProps={{ style: { fontSize: 16 } }} // Input styles
    />
  </DialogContent>
  <DialogActions>
    <span className='mx-3' onClick={handleCloseBillDialog} style={{ cursor: 'pointer', color: 'darkblue', fontWeight: '600', fontSize: 22 }}>Cancel</span>
    <span className='mx-3' onClick={handleSaveBillTo} style={{ cursor: 'pointer', color: 'darkblue', fontWeight: '600', fontSize: 22 }}>Save</span>
  </DialogActions>
</Dialog>

    </Container>
    </section>
  );
};

export default InvoiceGenerator;
